import dayjs from 'dayjs';
import { useRouter } from 'next/router';
import { ReactNode, RefObject, useMemo, useRef, useState } from 'react';
import { useRecoilState } from 'recoil';
import { SwiperRef } from 'swiper/react';

import { closedHospitalPopupDayState } from '@/recoil/atoms/common/popup';
import { isServer } from '@/types/common';
import { isBetweenDate, isSameDate } from '@/utils/date';
import { faceTofaceLogger, getPageName } from '@/utils/logger';
import {
  NATIVE_POST_MESSAGE_TYPE,
  isWebView,
  sendPostMessageToNative,
} from '@/utils/mobile/RNPlugins';
import { addQueryString } from '@/utils/url';

interface HospitalPopup {
  link: string;
  guideImage: string;
  startAt: string;
  endAt: string;
  buttonColor: string;
  buttonText: ReactNode;
  isExternalLink: boolean;
  payload?: Record<string, string | number>;
  isSameRouter: boolean;
}

interface HospitalNoticePopupViewModel {
  swiperRef: RefObject<SwiperRef>;
  currentStep: number;
  activePopupList: Array<HospitalPopup>;
  isClosedPopup: () => boolean;
  onChangeStep: (step: number) => void;
  onClosePopupToday: () => void;
  openLink: (popup: HospitalPopup) => void;
  popupPageViewLogger: () => void;
}

const useHospitalNoticePopupViewModel = (): HospitalNoticePopupViewModel => {
  const router = useRouter();
  const pageName = getPageName(router);
  const [closedHospitalPopupDay, setClosedHospitalPopupDay] = useRecoilState(
    closedHospitalPopupDayState,
  );

  const [currentStep, setCurrentStep] = useState<number>(0);
  const swiperRef = useRef<SwiperRef>(null);
  const isWebViewLink = isWebView();

  const hospitalPopupList: Array<HospitalPopup> = useMemo(() => {
    return [];
  }, [isWebViewLink]);

  const isClosedTodayHospitalPopup = useMemo(() => {
    if (!closedHospitalPopupDay) return false;
    return isSameDate(closedHospitalPopupDay, 'date');
  }, [closedHospitalPopupDay]);

  const activePopupList = useMemo(() => {
    return hospitalPopupList.filter((popup) =>
      isBetweenDate({
        startAt: popup.startAt,
        endAt: popup.endAt,
        unitType: 'minutes',
      }),
    );
  }, [hospitalPopupList]);

  const onChangeStep = (step: number) => {
    setCurrentStep(step);
  };

  const onClosePopupToday = () => {
    faceTofaceLogger({
      pageName,
      eventName: 'click_hospital_popup_close_today',
    });
    const today = dayjs().format();
    setClosedHospitalPopupDay(today);
  };

  const openLink = (popup: HospitalPopup) => {
    if (isServer) {
      return;
    }
    onClosePopupToday();

    faceTofaceLogger({
      pageName,
      eventName: 'hospital_popup_click',
      payload: {
        link: popup.link,
      },
    });

    if (popup.isExternalLink) {
      if (isWebViewLink) {
        sendPostMessageToNative(
          NATIVE_POST_MESSAGE_TYPE.OPEN_EXTERNAL_BROWSER,
          {
            url: popup.link,
          },
        );
        return;
      }

      window.open(popup.link);
      return;
    }

    if (isWebViewLink) {
      sendPostMessageToNative(
        popup.isSameRouter
          ? NATIVE_POST_MESSAGE_TYPE.REPLACE_TO_PAGE
          : NATIVE_POST_MESSAGE_TYPE.MOVE_TO_PAGE,
        {
          screenName: popup.link,
          ...(popup.payload && { params: { ...popup.payload } }),
        },
      );
      return;
    }

    router.push(`${popup.link}${addQueryString(popup.payload)}`);
  };

  const isClosedPopup = () => {
    return !activePopupList.length || isClosedTodayHospitalPopup;
  };

  const popupPageViewLogger = () => {
    faceTofaceLogger({
      pageName,
      eventName: 'hospital_popup_view',
    });
  };

  return {
    swiperRef,
    activePopupList,
    currentStep,
    isClosedPopup,
    onChangeStep,
    onClosePopupToday,
    openLink,
    popupPageViewLogger,
  };
};

export default useHospitalNoticePopupViewModel;
